/**
 * Inline-SVG game icons. Used by the floating reconnect card and any
 * other surface that needs a tiny game emblem next to a label. SVG
 * (vs. lucide / image asset) keeps it crisp at any zoom and lets the
 * stroke pick up the parent's `currentColor`.
 */

interface GameIconProps {
  game: "pong" | "fps" | "snake" | "tower-defense" | "math";
  className?: string;
}

export default function GameIcon({ game, className = "w-5 h-5" }: GameIconProps) {
  switch (game) {
    case "pong":
      // Paddle + ball. Paddle is the bold rectangle on the left, the
      // ball a filled disc trailing motion lines to the right.
      return (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className={className}
          aria-hidden="true"
        >
          <rect x="3" y="6" width="3" height="12" rx="1" fill="currentColor" />
          <circle cx="16" cy="12" r="3" fill="currentColor" />
          <path d="M10 9l2 0M10 15l2 0M9 12l1 0" opacity={0.5} />
        </svg>
      );

    case "fps":
      // Stylized handgun silhouette — barrel + grip + trigger guard.
      return (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className={className}
          aria-hidden="true"
        >
          <path d="M3 9 L17 9 L17 13 L13 13 L11 18 L7 18 L7 13 L3 13 Z" fill="currentColor" />
          <path d="M17 9 L21 9 L21 11 L17 11" />
        </svg>
      );

    case "snake":
      // S-curve serpent with a head dot.
      return (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          className={className}
          aria-hidden="true"
        >
          <path d="M4 18 C 4 14, 8 14, 8 12 C 8 10, 4 10, 4 8 C 4 6, 8 6, 12 6 C 16 6, 20 6, 20 10 C 20 14, 16 14, 16 16 C 16 18, 20 18, 20 18" />
          <circle cx="20.5" cy="18" r="1.2" fill="currentColor" />
        </svg>
      );

    case "tower-defense":
      // Castle/tower silhouette.
      return (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className={className}
          aria-hidden="true"
        >
          <path d="M4 20 L4 8 L7 8 L7 5 L10 5 L10 8 L14 8 L14 5 L17 5 L17 8 L20 8 L20 20 Z" fill="currentColor" />
          <rect x="10" y="13" width="4" height="7" fill="#0A0A0A" />
        </svg>
      );

    case "math":
      // Plus / square symbol — minimal "x + y" glyph stack.
      return (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className={className}
          aria-hidden="true"
        >
          <path d="M5 8h6M8 5v6" />
          <path d="M13 16h6" />
          <path d="M14 5 L19 10 M19 5 L14 10" />
        </svg>
      );
  }
}

/** Display name + accent color for the card chrome. */
export function gameMeta(game: GameIconProps["game"]) {
  switch (game) {
    case "pong":
      return { label: "Pong", accent: "#FF6B00", accent2: "#F54D12" };
    case "fps":
      return { label: "FPS", accent: "#22C55E", accent2: "#15803D" };
    case "snake":
      return { label: "Snake", accent: "#EAB308", accent2: "#A16207" };
    case "tower-defense":
      return { label: "Tower Defense", accent: "#8B5CF6", accent2: "#6D28D9" };
    case "math":
      return { label: "Mental Math", accent: "#FF5722", accent2: "#E94A18" };
  }
}
